import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Anchor, Button, Card, Divider, Group, Stack, Text } from '@mantine/core';
import {
  IconAddressBook,
  IconBrandFacebook,
  IconBrandGoogle,
  IconBrandInstagram,
  IconBrandWhatsapp,
  IconBuildingStore,
  IconCode,
  IconStethoscope,
  IconWebhook,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { trpc } from '../../lib/trpc';
import { PermissionGate } from '../../components/PermissionGate';
import { PageHeader } from '../../components/common/PageHeader';
import { QueryBoundary } from '../../components/common/QueryBoundary';
import { AdvancedSection } from '../../components/common/AdvancedSection';
import { ConnectionRow, type ConnectionHealth } from '../../components/connections/ConnectionRow';
import { channelHealth, expiryDetail } from '../../lib/connection-health';
import { HealthSummary } from '../../components/connections/HealthSummary';
import { ManageDrawer, type ManageChannel } from '../../components/connections/ManageDrawer';
import { OutgoingWebhookPanel } from '../../components/webhooks/OutgoingWebhookPanel';
import { DeliveryLogPanel } from '../../components/webhooks/DeliveryLogPanel';

/**
 * Every connection LeadSync has, on one page, in one shape.
 *
 * Replaces — but does not delete — the five-tab Meta page. Two of those tabs
 * (outgoing webhooks, delivery log) are diagnostics for people who are already
 * connected, so a first-time vendor met a tab bar before they met a task; here
 * they are folded into a single Advanced disclosure at the bottom, alongside
 * the connection checks that currently live on their own nav entry.
 *
 * Google, e-commerce and CRM read exactly the queries their own pages read.
 * Nothing about them changes except that they now look like everything else.
 */
function ConnectionsHub() {
  const navigate = useNavigate();
  const [manage, setManage] = useState<ManageChannel | null>(null);

  const utils = trpc.useUtils();
  const status = trpc.integrations.meta.status.useQuery();
  const google = trpc.integrations.google.status.useQuery();
  const stores = trpc.ecommerce.list.useQuery();
  const crm = trpc.contacts.crm.list.useQuery();

  const refresh = () => {
    void utils.integrations.meta.status.invalidate();
    void utils.integrations.meta.getWhatsAppManualConfig.invalidate();
  };

  const wa = status.data?.whatsapp[0] ?? null;
  const fb = status.data?.facebook[0] ?? null;
  const ig = status.data?.instagram[0] ?? null;

  const waHealth: ConnectionHealth = channelHealth({
    connected: !!wa,
    pending: wa?.status !== 'active',
    receiving: !!status.data?.isWebhookVerified,
    tokenExpiresAt: wa?.tokenExpiresAt,
  });
  const fbHealth: ConnectionHealth = channelHealth({
    connected: !!fb,
    receiving: !!fb?.webhookSubscribedAt,
    tokenExpiresAt: fb?.tokenExpiresAt,
  });
  const igHealth: ConnectionHealth = channelHealth({
    connected: !!ig,
    receiving: !!ig?.webhookSubscribedAt,
    tokenExpiresAt: ig?.tokenExpiresAt,
  });

  const messaging: ConnectionHealth[] = [waHealth, fbHealth, igHealth];
  const connectedCount = messaging.filter((h) => h !== 'disconnected').length;
  const attentionCount = messaging.filter((h) => h === 'attention').length;

  const activeStores = (stores.data ?? []).filter((s) => s.status === 'active');
  const activeCrm = (crm.data ?? []).filter((c) => c.status === 'active');

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="lg" maw={1000} mx="auto" pb="xl">
        <PageHeader
          title="Connections"
          description="Everything LeadSync is linked to."
          actions={
            <Button component={Link} to="/vendor/setup" variant="light">
              Connect a channel
            </Button>
          }
        />

        <QueryBoundary
          isPending={status.isPending}
          isError={status.isError}
          error={status.error}
          errorMessage="Couldn't load your connections."
          onRetry={refresh}
        >
          <Stack gap="lg">
            <HealthSummary connectedCount={connectedCount} attentionCount={attentionCount} />

            <Card withBorder radius="md" padding="md">
              <Stack gap={0}>
                <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb="xs">
                  Messaging
                </Text>

                <ConnectionRow
                  icon={<IconBrandWhatsapp size={20} />}
                  name="WhatsApp"
                  identity={wa?.displayPhoneNumber}
                  detail={
                    expiryDetail(wa?.tokenExpiresAt) ??
                    (waHealth === 'attention'
                      ? "Incoming messages aren't reaching you."
                      : (wa?.verifiedName ?? null))
                  }
                  health={waHealth}
                  onManage={() => setManage('whatsapp')}
                  onConnect={() => void navigate({ to: '/vendor/setup' })}
                />
                <Divider />
                <ConnectionRow
                  icon={<IconBrandFacebook size={20} />}
                  name="Messenger"
                  identity={fb?.pageName}
                  detail={
                    expiryDetail(fb?.tokenExpiresAt) ??
                    (fbHealth === 'attention'
                      ? "Incoming messages aren't reaching you."
                      : fb?.category)
                  }
                  health={fbHealth}
                  onManage={() => setManage('facebook')}
                  onConnect={() => void navigate({ to: '/vendor/setup' })}
                />
                <Divider />
                <ConnectionRow
                  icon={<IconBrandInstagram size={20} />}
                  name="Instagram"
                  identity={ig?.username ? `@${ig.username}` : null}
                  detail={
                    expiryDetail(ig?.tokenExpiresAt) ??
                    (igHealth === 'attention' ? "Incoming messages aren't reaching you." : ig?.name)
                  }
                  health={igHealth}
                  onManage={() => setManage('instagram')}
                  onConnect={() => void navigate({ to: '/vendor/setup' })}
                />
              </Stack>
            </Card>

            <Card withBorder radius="md" padding="md">
              <Stack gap={0}>
                <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb="xs">
                  Business tools
                </Text>

                <ConnectionRow
                  icon={<IconBrandGoogle size={20} />}
                  name="Google"
                  identity={google.data?.email}
                  detail={
                    google.data?.connected
                      ? `${google.data.locations.length} location${google.data.locations.length === 1 ? '' : 's'}`
                      : null
                  }
                  health={google.data?.connected ? 'working' : 'disconnected'}
                  onManage={() => void navigate({ to: '/vendor/integrations/google' })}
                  onConnect={() => void navigate({ to: '/vendor/integrations/google' })}
                />
                <Divider />
                <ConnectionRow
                  icon={<IconBuildingStore size={20} />}
                  name="Online store"
                  identity={activeStores[0]?.shopDomain}
                  detail={
                    activeStores.length > 1
                      ? `+${activeStores.length - 1} more`
                      : activeStores[0]?.provider
                  }
                  health={activeStores.length > 0 ? 'working' : 'disconnected'}
                  onManage={() => void navigate({ to: '/vendor/integrations/ecommerce' })}
                  onConnect={() => void navigate({ to: '/vendor/integrations/ecommerce' })}
                />
                <Divider />
                <ConnectionRow
                  icon={<IconAddressBook size={20} />}
                  name="Your CRM"
                  identity={activeCrm[0]?.provider}
                  detail={activeCrm.length > 1 ? `+${activeCrm.length - 1} more` : null}
                  health={activeCrm.length > 0 ? 'working' : 'disconnected'}
                  onManage={() => void navigate({ to: '/vendor/contacts/crm' })}
                  onConnect={() => void navigate({ to: '/vendor/contacts/crm' })}
                />
              </Stack>
            </Card>

            {/* Tier 3. Everything here used to be two tabs and a nav entry of
                its own; none of it is useful until something is wrong. */}
            <AdvancedSection
              title="Developer"
              description="Connection checks, outgoing webhooks, delivery log and API keys."
            >
              <Stack gap="lg">
                <Group gap="sm">
                  <Button
                    size="xs"
                    variant="default"
                    leftSection={<IconStethoscope size={14} />}
                    component={Link}
                    to="/vendor/connection-test"
                  >
                    Run a connection check
                  </Button>
                  <Button
                    size="xs"
                    variant="default"
                    leftSection={<IconCode size={14} />}
                    component={Link}
                    to="/vendor/integrations/api"
                  >
                    API keys
                  </Button>
                </Group>

                <Divider label="Outgoing webhooks" labelPosition="left" />
                <OutgoingWebhookPanel />

                <Divider label="Delivery log" labelPosition="left" />
                <DeliveryLogPanel />

                <Group gap={6}>
                  <IconWebhook size={14} />
                  <Anchor component={Link} to="/vendor/integrations/meta" size="xs" c="dimmed">
                    Open the classic Meta setup page
                  </Anchor>
                </Group>
              </Stack>
            </AdvancedSection>
          </Stack>
        </QueryBoundary>

        <ManageDrawer
          opened={manage !== null}
          channel={manage}
          onClose={() => setManage(null)}
          onChanged={refresh}
        />
      </Stack>
    </motion.div>
  );
}

function ConnectionsGated() {
  return (
    <PermissionGate permission="manage_integrations">
      <ConnectionsHub />
    </PermissionGate>
  );
}

export const Route = createFileRoute('/vendor/connections')({ component: ConnectionsGated });
